import { defineStore } from "pinia";
import {
  Customer,
  CustomerNote,
  CustomerStore,
  CreateCustomer,
  CustomerSearchParams,
  CustomerSearchResponse,
  ListCustomersResponse,
  UpdateCustomerPayload,
} from "@/customers/types/customer";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import { db } from "@/app/plugins/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import type { AxiosRequestConfig } from "axios";
import type { AddCustomerNoteRequest } from "@nectarpay/api-contracts";

export type CustomerIdentifier =
  | string
  | { id?: string | null }
  | (string | null)[]
  | null
  | undefined;

const normalizeCustomerId = (id: CustomerIdentifier): string | null => {
  if (Array.isArray(id)) {
    return id.find((value) => typeof value === "string" && value.trim()) ?? null;
  }

  if (typeof id === "object" && id !== null) {
    return id.id ?? null;
  }

  return id ?? null;
};

const buildCustomerSearchQuery = (searchTerm: string): string => {
  const sanitizedTerm = searchTerm.trim().replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  return `name~"${sanitizedTerm}" OR email~"${sanitizedTerm}" OR phone~"${sanitizedTerm}"`;
};

export const useCustomersStore = defineStore("customers", {
  state: (): CustomerStore => ({
    customers: [],
    loading: false,
    listLoading: false,
    filterCustomerCondition: {},
    noResults: false,
    hasMore: false,
    startingAfter: "",
    endingBefore: "",
    nextPage: "",
    searchCurrentPage: "",
    query: null,
    notes: [],
    requestCounter: 0,
  }),

  getters: {
    isLoading: (state) => state.loading,
    getCustomers: (state) => state.customers,
  },

  actions: {
    setQuery(query: string): void {
      this.query = query;
    },

    invalidateRequests(): void {
      this.requestCounter += 1;
      this.loading = false;
      this.listLoading = false;
    },

    clearCustomerResults(): void {
      this.customers = [];
      this.hasMore = false;
      this.nextPage = "";
      this.startingAfter = "";
      this.endingBefore = "";
      this.noResults = false;
    },

    async createCustomer(payload: CreateCustomer): Promise<Customer | undefined> {
      try {
        const response = await axios.post<Customer>("/customer", payload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async listCustomers(apiOptions?: AxiosRequestConfig): Promise<"success" | "stale" | "error"> {
      const requestId = this.requestCounter + 1;
      this.requestCounter = requestId;
      this.loading = true;
      this.noResults = false;
      try {
        const response = await axios.get<ListCustomersResponse>("/customer", apiOptions);
        if (requestId !== this.requestCounter) {
          return "stale";
        }
        const customers = Array.isArray(response.data?.data) ? response.data.data : [];
        this.nextPage = response.data.next_page ?? "";
        this.hasMore = response.data.has_more;
        this.noResults = customers.length === 0;
        this.customers = [...customers];
        this.startingAfter = String(customers[customers.length - 1]?.id || "");
        this.endingBefore = String(customers[0]?.id || "");
        return "success";
      } catch (error) {
        if (requestId !== this.requestCounter) {
          return "stale";
        }
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return "error";
      } finally {
        if (requestId === this.requestCounter) {
          this.loading = false;
        }
      }
    },

    // Not sure if this function is needed
    async fetchCustomers(): Promise<Customer[] | undefined> {
      this.loading = true;
      try {
        const response = await axios.get<ListCustomersResponse>("/customer");
        return response.data?.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async retrieveCustomer(id: CustomerIdentifier): Promise<Customer | undefined> {
      const customerId = normalizeCustomerId(id);
      if (!customerId) {
        return undefined;
      }

      try {
        const response = await axios.get<Customer>(`/customer/${customerId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async updateCustomer(
      id: string,
      payload: UpdateCustomerPayload,
    ): Promise<Customer | undefined> {
      try {
        const response = await axios.patch<Customer>(`/customer/${id}`, payload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async searchCustomers(apiOptions?: AxiosRequestConfig): Promise<"success" | "stale" | "error"> {
      const requestId = this.requestCounter + 1;
      this.requestCounter = requestId;
      this.listLoading = true;
      this.noResults = false;
      try {
        if (!this.query) {
          const response = await axios.get<ListCustomersResponse>("/customer", apiOptions);
          if (requestId !== this.requestCounter) {
            return "stale";
          }
          const customers = Array.isArray(response.data?.data) ? response.data.data : [];
          this.nextPage = response.data.next_page ?? "";
          this.hasMore = response.data.has_more;
          this.startingAfter = String(customers[customers.length - 1]?.id || "");
          this.endingBefore = String(customers[0]?.id || "");
          this.noResults = customers.length === 0;
          this.customers = [...customers];
          return "success";
        }

        const params = (apiOptions?.params ?? {}) as { page?: string; limit?: number };
        const payload: {
          query: string;
          limit?: number;
          page?: string;
        } = {
          query: buildCustomerSearchQuery(this.query),
        };

        if (params.page) {
          payload.page = params.page;
        }

        if (params.limit) {
          payload.limit = params.limit;
        }

        const response = await axios.post<CustomerSearchResponse>(`/customer/search`, payload);
        if (requestId !== this.requestCounter) {
          return "stale";
        }
        const customers = Array.isArray(response.data?.data) ? response.data.data : [];
        this.hasMore = response.data.has_more;
        this.nextPage = response.data.next_page ?? "";
        this.startingAfter = String(customers[customers.length - 1]?.id || "");
        this.endingBefore = String(customers[0]?.id || "");
        this.noResults = customers.length === 0;
        this.customers = [...customers];
        return "success";
      } catch (error) {
        if (requestId !== this.requestCounter) {
          return "stale";
        }
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return "error";
      } finally {
        if (requestId === this.requestCounter) {
          this.listLoading = false;
        }
      }
    },

    async searchCustomersPage(payload: CustomerSearchParams): Promise<CustomerSearchResponse> {
      const requestPayload: {
        query: string;
        limit?: number;
        page?: string;
      } = {
        query: buildCustomerSearchQuery(payload.searchTerm),
      };

      if (payload.limit) {
        requestPayload.limit = payload.limit;
      }

      if (payload.page) {
        requestPayload.page = payload.page;
      }

      const response = await axios.post<CustomerSearchResponse>("/customer/search", requestPayload);
      return response.data;
    },

    async deleteCustomer(id: string): Promise<Customer | undefined> {
      try {
        const response = await axios.delete<Customer>(`/customer/${id}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async addCustomerNote(
      id: string,
      data: AddCustomerNoteRequest,
    ): Promise<CustomerNote | undefined> {
      try {
        const response = await axios.post<CustomerNote>(`/customer/note/${id}`, data);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async listenToUserNotes(accountId: string | null | undefined, customerId: unknown) {
      const normalizedCustomerId = normalizeCustomerId(customerId as CustomerIdentifier);
      if (!accountId || !normalizedCustomerId) {
        return;
      }
      const notesRef = collection(
        db,
        "accounts",
        accountId,
        "customers",
        normalizedCustomerId,
        "notes",
      );

      onSnapshot(notesRef, (snapshot) => {
        const notes = snapshot.docs.map((doc) => doc.data() as CustomerNote);
        this.notes = [...notes];
      });
    },

    async prefillCustomer(value: string, fieldType: string): Promise<Customer[]> {
      this.loading = true;
      try {
        const fieldQuery = `${fieldType}:"${value}"`;
        const payload = { query: fieldQuery };
        const response = await axios.post<CustomerSearchResponse>(`/customer/search`, payload);
        return response.data?.data ?? [];
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return [];
      } finally {
        this.loading = false;
      }
    },
  },
});
